import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";

const LocalMap = lazy(() => import("../components/LocalMap"));
import { 
  ArrowRight, Sparkles, Receipt, 
  Gift, Wallet, CheckCircle2, Refrigerator, UtensilsCrossed, 
  Wine, Calendar, Store, Users, Star, ShieldCheck, MapPin,
  Search, Clock, ShoppingBag, Leaf, ChefHat, Utensils, Tag, BookOpen
} from "lucide-react";
import { GustoLogo } from "@/components/GustoLogo";
import GustoFooter from "@/components/GustoFooter";
import gustoAvatar from "@/assets/gusto-avatar.png";
import gustoAvatarWhite from "@/assets/gusto-avatar-white.png";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { toast } from "sonner";

const HomePage = () => {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [stores, setStores] = useState([]);
  const [shopProducts, setShopProducts] = useState([]);
  const [dailySpecials, setDailySpecials] = useState([]);
  const [antiWasteBoxes, setAntiWasteBoxes] = useState([]);
  const [storeCategory, setStoreCategory] = useState("all");
  const [storePostalCode, setStorePostalCode] = useState(user?.postal_code || "13800");
  const [loading, setLoading] = useState(true);

  const STORE_CATEGORIES = [
    { id: "all", label: "Tous" },
    { id: "restaurant", label: "Restaurants" },
    { id: "supermarche", label: "Supermarchés" },
    { id: "producteur", label: "Producteurs" },
    { id: "primeur", label: "Primeurs" },
    { id: "boulangerie", label: "Boulangeries" },
    { id: "boucherie", label: "Boucheries" },
    { id: "bio", label: "Bio" },
    { id: "discount", label: "Discount" },
    { id: "grossiste", label: "Grossistes" },
    { id: "epicerie", label: "Épiceries" },
    { id: "caviste", label: "Cavistes" },
  ];

  const fetchStores = useCallback(async (cat, cp, radius) => {
    try {
      const params = new URLSearchParams();
      if (cat && cat !== "all") params.set("category", cat);
      const effectiveCp = cp || (user?.postal_code || "13800");
      if (effectiveCp) params.set("postal_code", effectiveCp);
      if (radius) params.set("radius_km", radius.toString());
      if (!effectiveCp && navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => 
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 3000 })
          );
          params.set("lat", pos.coords.latitude);
          params.set("lng", pos.coords.longitude);
        } catch {}
      }
      const res = await axios.get(`${API}/stores/directory?${params.toString()}`);
      setStores(res.data.slice(0, 50));
    } catch {}
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [recipesRes] = await Promise.all([
          axios.get(`${API}/feed`),
        ]);
        setRecipes(recipesRes.data);
        try {
          const bizRes = await axios.get(`${API}/businesses`);
          setBusinesses(bizRes.data || []);
        } catch {}
        try {
          const shopRes = await axios.get(`${API}/shop/products`);
          setShopProducts(shopRes.data || []);
        } catch {}
        try {
          const [dsRes, awRes] = await Promise.all([
            axios.get(`${API}/daily-specials`),
            axios.get(`${API}/anti-waste-boxes`)
          ]);
          setDailySpecials(dsRes.data || []);
          setAntiWasteBoxes(awRes.data || []);
        } catch {}
        await fetchStores("all", "13800", 30);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fetchStores]);

  const handleStoreFilter = (cat) => {
    setStoreCategory(cat);
    fetchStores(cat, storePostalCode);
  };

  const handlePostalFilter = () => {
    fetchStores(storeCategory, storePostalCode);
  };

  const appJourney = [
    {
      step: "01",
      icon: Receipt,
      title: "Scannez & suivez vos achats",
      description: "Scannez vos tickets de caisse pour récupérer automatiquement les produits, prix et date. Recevez des alertes rappel produit et suivez votre budget courses en temps réel.",
      color: "from-amber-500 to-orange-600",
      bgColor: "bg-amber-50",
      features: ["Scan IA", "Alerte rappel produit", "Suivi budget", "Comparateur de prix"]
    },
    {
      step: "02",
      icon: UtensilsCrossed,
      title: "Cuisinez & partagez vos recettes",
      description: "Dictez vos recettes vocalement pendant que vous cuisinez. Scannez un plat pour obtenir sa recette et ses calories. La communauté partage, note et reproduit vos créations.",
      color: "from-primary to-red-500",
      bgColor: "bg-primary/5",
      features: ["Dictée vocale", "Scanner plat IA", "Menu semaine", "Partage communautaire"]
    },
    {
      step: "03",
      icon: Calendar,
      title: "Organisez des repas entre amis",
      description: "Planifiez un repas, invitez vos amis et partagez la liste de courses. Les allergies et préférences de chaque invité sont connues : zéro faux pas garanti !",
      color: "from-lavender to-purple-500",
      bgColor: "bg-lavender/15",
      features: ["Liste partagée", "Allergènes invités", "Notes des convives", "Budget participatif"]
    },
    {
      step: "04",
      icon: ChefHat,
      title: "Découvrez les restaurants & plats du jour",
      description: "Explorez les restaurants autour de vous : menus, avis détaillés (cuisine, service, ambiance), plats du jour et offres exclusives. Réservez directement via la carte interactive.",
      color: "from-red-500 to-rose-600",
      bgColor: "bg-red-50",
      features: ["Menus & avis", "Plats du jour", "Carte interactive", "Click & Collect"]
    },
    {
      step: "05",
      icon: Leaf,
      title: "Luttez contre le gaspillage",
      description: "Réservez des colis anti-gaspi à prix réduits chez vos commerces préférés. 1 colis par mois, des produits de qualité à petits prix. Les pros s'engagent et y gagnent aussi !",
      color: "from-emerald-500 to-green-600",
      bgColor: "bg-green-50",
      features: ["Colis à prix réduits", "1/mois par membre", "Retrait sur place", "Licence offerte aux pros"]
    },
    {
      step: "06",
      icon: Store,
      title: "Soutenez vos commerces locaux",
      description: "Producteurs, boulangers, cavistes, épiciers : retrouvez tous les artisans locaux dans l'annuaire. Filtrez par rayon, comparez les prix et profitez de leurs offres exclusives.",
      color: "from-charcoal to-charcoal/80",
      bgColor: "bg-gray-50",
      features: ["Annuaire local", "Filtrage par rayon", "Offres & promos", "Fidélisation"]
    }
  ];

  const quickFeatures = [
    { icon: Refrigerator, title: "Scanner frigo", desc: "Recettes selon vos ingrédients", link: "/fridge-scanner", color: "from-cyan-500 to-blue-600" },
    { icon: UtensilsCrossed, title: "Scanner plat", desc: "Calories & recette", link: "/dish-scanner", color: "from-lavender to-purple-500" },
    { icon: Receipt, title: "Scanner ticket", desc: "Suivi achats & budget", link: "/my-receipts", color: "from-amber-500 to-orange-600" },
    { icon: Calendar, title: "Menu semaine", desc: "Planifiez vos repas", link: "/weekly-menu", color: "from-green-500 to-emerald-600" },
    { icon: Wine, title: "Ma Cave", desc: "Gérez vos vins", link: "/wine-cellar", color: "from-red-600 to-rose-700" },
    { icon: Leaf, title: "Anti-Gaspi", desc: "Colis à prix réduits", link: "/#anti-gaspi", color: "from-emerald-500 to-teal-600" },
    { icon: Tag, title: "Coupons", desc: "Bons plans près de chez vous", link: "/coupons", color: "from-rose-500 to-red-600" },
    { icon: BookOpen, title: "Guide", desc: "Comment ça marche", link: "/guide", color: "from-indigo-500 to-purple-600" },
  ];

  // Separate restaurants from other businesses
  const restaurantBusinesses = businesses.filter(b => b.business_type === "restaurant");
  const otherBusinesses = businesses.filter(b => b.business_type !== "restaurant");

  // Helper to get display name for a business
  const bizName = (b) => b.business_name || b.name || "Commerce";

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <motion.img
          src={gustoAvatar}
          alt="Chargement..."
          className="w-16 h-16"
          animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section with Video Background */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        {/* Local Video Background */}
        <div className="absolute inset-0 z-0 overflow-hidden" style={{ background: "linear-gradient(135deg, #1e1432 0%, #140e28 50%, #261840 100%)" }}>
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/videos/hero-cooking.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
          {/* Dark lavender/black transparent overlay */}
          <div className="absolute inset-0 z-10" style={{ background: "linear-gradient(135deg, rgba(40,30,60,0.82) 0%, rgba(30,20,50,0.75) 50%, rgba(50,35,70,0.80) 100%)" }} />
        </div>
        
        <div className="relative z-20 px-4 md:px-8 max-w-4xl mx-auto text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* White Avatar - 250% size */}
            <motion.img
              src={gustoAvatarWhite}
              alt="Gusto"
              className="w-28 h-28 md:w-36 md:h-36 mx-auto mb-6 drop-shadow-2xl"
              animate={{ scale: [1, 1.05, 1], rotate: [0, 2, -2, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
            
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
              Bienvenue Chef(fe) !
            </h1>
            
            <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-8">
              A vous de jouer et d'épater vos convives !
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={user ? "/discover" : "/register"}>
                <Button 
                  size="lg" 
                  className="bg-white text-charcoal hover:bg-white/90 rounded-full h-12 px-6 shadow-lg font-heading font-bold"
                >
                  {user ? "Découvrir" : "Commencer gratuitement"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Access Features */}
      <section className="py-10 px-4 bg-gradient-to-b from-white to-lavender/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-charcoal">
              Accès rapide
            </h2>
            <p className="text-muted-foreground text-sm mt-1">Les outils IA à portée de main</p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {quickFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.07 }}
              >
                <Link to={user ? feature.link : "/login"}>
                  <Card className="h-full hover:shadow-lg transition-all group cursor-pointer border-lavender/30">
                    <CardContent className="p-3 md:p-4 text-center">
                      <div className={`w-11 h-11 md:w-12 md:h-12 mx-auto rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                        <feature.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                      </div>
                      <h3 className="font-semibold text-charcoal text-xs md:text-sm">{feature.title}</h3>
                      <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5">{feature.desc}</p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Map + Daily Specials & Anti-Waste Boxes */}
      <section className="py-10 px-4 bg-white">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Interactive Map */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">🗺️</span>
              <h2 className="font-heading text-xl font-bold text-charcoal">Autour de vous</h2>
            </div>
              <Suspense fallback={<div className="h-[400px] rounded-2xl bg-gray-100 animate-pulse flex items-center justify-center text-muted-foreground">Chargement de la carte...</div>}>
                <LocalMap
                  dailySpecials={dailySpecials}
                  antiWasteBoxes={antiWasteBoxes}
                  stores={stores}
                  businesses={businesses}
                  userPostalCode={user?.postal_code || storePostalCode || "13800"}
                />
              </Suspense>
            </div>
            {/* Daily Specials */}
            {dailySpecials.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">☀️</span>
                  <h2 className="font-heading text-xl font-bold text-charcoal">Plats du jour</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {dailySpecials.slice(0, 4).map(special => (
                    <Link key={special.id} to={`/business/${special.business_id}`}>
                      <Card className="hover:shadow-lg transition-shadow bg-gradient-to-br from-amber-50 to-orange-50" data-testid={`daily-special-${special.id}`}>
                        <CardContent className="p-4 flex items-center gap-3">
                          {special.logo ? (
                            <img src={special.logo} alt="" className="w-12 h-12 rounded-xl object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-amber-200 flex items-center justify-center text-xl">🍽️</div>
                          )}
                          <div className="flex-1 min-w-0">
                            <Badge className="bg-amber-500 text-white text-[9px] mb-1">Plat du jour</Badge>
                            <p className="font-semibold text-sm truncate">{special.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{special.business_name} — {special.city}</p>
                          </div>
                          {special.price && <p className="text-lg font-bold text-amber-600">{special.price.toFixed(2)}€</p>}
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Anti-Waste Boxes */}
            {antiWasteBoxes.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🌿</span>
                  <h2 className="font-heading text-xl font-bold text-charcoal">Colis Anti-Gaspi</h2>
                  <span className="text-xs text-muted-foreground ml-auto">1 colis/mois max</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {antiWasteBoxes.slice(0, 4).map(box => (
                    <Card key={box.id} className="hover:shadow-lg transition-shadow bg-gradient-to-br from-green-50 to-emerald-50" data-testid={`anti-waste-home-${box.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          {box.logo ? (
                            <img src={box.logo} alt="" className="w-12 h-12 rounded-xl object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-emerald-200 flex items-center justify-center text-xl">🌿</div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-emerald-500 text-white text-[9px]">Anti-Gaspi</Badge>
                              {box.discount_percent && <Badge className="bg-red-500 text-white text-[9px]">-{box.discount_percent}%</Badge>}
                            </div>
                            <p className="font-semibold text-sm truncate mt-1">{box.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{box.business_name} — {box.city}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs line-through text-muted-foreground">{box.original_value?.toFixed(2)}€</p>
                            <p className="text-lg font-bold text-emerald-600">{box.price?.toFixed(2)}€</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t">
                          <span className="text-xs text-muted-foreground">Retrait : {box.pickup_start} - {box.pickup_end}</span>
                          {user ? (
                            <Button
                              size="sm"
                              className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs h-7 px-3"
                              onClick={async (e) => {
                                e.preventDefault();
                                try {
                                  const t = localStorage.getItem("token");
                                  await axios.post(`${API}/anti-waste-boxes/${box.id}/reserve`, {}, {
                                    headers: { Authorization: `Bearer ${t}` }
                                  });
                                  toast.success("Colis réservé ! Présentez-vous aux horaires de retrait.");
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || "Erreur de réservation");
                                }
                              }}
                              data-testid={`reserve-aw-${box.id}`}
                            >
                              Réserver
                            </Button>
                          ) : (
                            <Link to="/login"><Button size="sm" variant="outline" className="text-xs h-7 px-3">Se connecter</Button></Link>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

      {/* App Journey */}
      <section className="py-12 px-4 bg-white" id="how-it-works">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-lavender/30 text-charcoal border-0 px-4 py-2 mb-4">
              Comment ça marche
            </Badge>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-charcoal">
              Votre parcours avec Gusto
            </h2>
            <p className="text-muted-foreground text-sm mt-2">
              Découvrez tout ce que vous pouvez faire avec l'application
            </p>
          </div>

          <div className="space-y-5">
            {appJourney.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <Card className={`overflow-hidden border-0 shadow-md ${item.bgColor}`}>
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      <div className={`bg-gradient-to-br ${item.color} p-5 md:p-6 flex flex-col items-center justify-center md:w-40`}>
                        <span className="text-white/60 text-xs font-medium mb-1">Étape {item.step}</span>
                        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                          <item.icon className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <div className="p-5 flex-1">
                        <h3 className="font-heading text-lg font-bold text-charcoal mb-1.5">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-3">{item.description}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {item.features.map((feature, idx) => (
                            <span 
                              key={idx}
                              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white text-xs text-charcoal shadow-sm"
                            >
                              <CheckCircle2 className="w-3 h-3 text-green-500" />
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Chef Gusto Section */}
      <section className="py-10 px-4 bg-gradient-to-r from-charcoal to-charcoal/90">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-5 text-white">
            <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 ring-2 ring-white/20">
              <img src={gustoAvatarWhite} alt="Chef Gusto" className="w-16 h-16 object-contain" />
            </div>
            <div className="text-center md:text-left flex-1">
              <h2 className="font-heading text-xl font-bold mb-1.5">
                Rencontrez Chef Gusto
              </h2>
              <p className="text-white/80 text-sm">
                Votre assistant culinaire IA. Scannez un plat et Chef Gusto vous 
                donne la recette complète avec les calories. Pas d'idée ? 
                Il génère des recettes à partir de vos ingrédients.
              </p>
            </div>
            <Link to={user ? "/dish-scanner" : "/login"}>
              <Button className="bg-white text-charcoal hover:bg-white/90 rounded-full px-5 text-sm">
                Essayer <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ====== RESTAURANTS SECTION ====== */}
      {restaurantBusinesses.length > 0 && (
        <section className="py-10 px-4 bg-gradient-to-b from-white to-red-50/30" data-testid="restaurants-section">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-5">
              <div>
                <Badge className="bg-red-100 text-red-700 border-0 px-3 py-1.5 mb-2">
                  <ChefHat className="w-3 h-3 mr-1.5" />
                  Restaurants
                </Badge>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-charcoal">
                  Les restaurants à découvrir
                </h2>
                <p className="text-sm text-muted-foreground mt-1">Menus, plats du jour et avis de la communauté</p>
              </div>
              <Link to="/stores?category=restaurant">
                <Button variant="outline" size="sm" className="rounded-full gap-1 text-xs">
                  Tous les restos <ArrowRight className="w-3 h-3" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {restaurantBusinesses.slice(0, 6).map((resto, i) => (
                <motion.div key={resto.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link to={`/stores/${resto.id}`} data-testid={`home-resto-${resto.id}`}>
                    <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full border-red-100/50">
                      <div className="aspect-[16/10] bg-gray-100 relative overflow-hidden">
                        {resto.cover_image ? (
                          <img src={resto.cover_image} alt={bizName(resto)} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
                            <ChefHat className="w-10 h-10 text-red-200" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                        <div className="absolute bottom-2 left-2 flex gap-1.5">
                          <Badge className="bg-white/90 text-charcoal text-[10px] px-1.5 backdrop-blur-sm">
                            <Utensils className="w-2.5 h-2.5 mr-0.5" />
                            Restaurant
                          </Badge>
                          {resto.has_daily_special && (
                            <Badge className="bg-amber-400 text-charcoal text-[10px] px-1.5">
                              <Star className="w-2.5 h-2.5 mr-0.5" />
                              Plat du jour
                            </Badge>
                          )}
                        </div>
                      </div>
                      <CardContent className="p-3">
                        <div className="flex items-start gap-2">
                          <div className="w-9 h-9 rounded-lg bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden -mt-5 relative z-10 shadow-sm">
                            {resto.logo ? (
                              <img src={resto.logo} alt="" className="w-full h-full object-contain p-0.5" />
                            ) : (
                              <ChefHat className="w-4 h-4 text-red-300" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-semibold text-sm text-charcoal truncate">{bizName(resto)}</h3>
                            {resto.cuisine_type && (
                              <p className="text-[10px] text-primary font-medium">{resto.cuisine_type}</p>
                            )}
                          </div>
                          {resto.review_avg > 0 && (
                            <div className="flex items-center gap-0.5 text-xs">
                              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                              <span className="font-medium">{resto.review_avg?.toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1.5 truncate">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          {resto.postal_code} {resto.city}
                        </p>
                        {resto.price_range && (
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {"€".repeat(resto.price_range || 2)} {resto.pmr_accessible ? "· Accès PMR" : ""}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ====== COMMERCES LOCAUX (non-restaurants) ====== */}
      <section className="py-10 px-4 bg-white" data-testid="stores-carousel-section">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-charcoal">
                Commerces près de chez vous
              </h2>
              <p className="text-sm text-muted-foreground mt-1">Producteurs, boulangers, cavistes et plus encore</p>
            </div>
            <Link to="/stores">
              <Button variant="outline" size="sm" className="rounded-full gap-1 text-xs">
                Voir tout <ArrowRight className="w-3 h-3" />
              </Button>
            </Link>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide flex-1">
              {STORE_CATEGORIES.filter(c => c.id !== "restaurant").map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleStoreFilter(cat.id)}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    storeCategory === cat.id
                      ? "bg-primary text-white"
                      : "bg-gray-100 text-charcoal hover:bg-gray-200"
                  }`}
                  data-testid={`home-cat-${cat.id}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <Input
                value={storePostalCode}
                onChange={(e) => setStorePostalCode(e.target.value)}
                placeholder="Code postal"
                className="w-32 h-8 text-xs"
                onKeyDown={(e) => e.key === "Enter" && handlePostalFilter()}
                data-testid="home-postal-input"
              />
              <Button size="sm" variant="outline" onClick={handlePostalFilter} className="h-8 w-8 p-0">
                <Search className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* 3x3 Grid - Non-restaurant stores */}
          {otherBusinesses.length === 0 && stores.length === 0 ? (
            <div className="text-center py-12">
              <Store className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground text-sm">Aucun commerce trouvé</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(otherBusinesses.length > 0 ? otherBusinesses : stores).slice(0, 9).map((store, i) => (
                <motion.div key={store.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Link to={`/stores/${store.id}`} data-testid={`home-store-${store.id}`}>
                    <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full">
                      <div className="aspect-[16/10] bg-gray-100 relative overflow-hidden">
                        {store.cover_image ? (
                          <img
                            src={store.cover_image}
                            alt={store.name}
                            className={`w-full h-full object-cover ${!store.claimed ? "blur-[6px] scale-105" : ""}`}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                            <img src={gustoAvatarWhite} alt="" className="w-12 h-12 opacity-20" />
                          </div>
                        )}
                        {/* Salmon overlay */}
                        <div className="absolute inset-0 bg-primary/25" />
                        {!store.claimed && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <img src={gustoAvatarWhite} alt="" className="w-10 h-10 opacity-40 drop-shadow-lg" />
                          </div>
                        )}
                        {!store.claimed && (
                          <Badge className="absolute top-1.5 right-1.5 bg-amber-100 text-amber-700 text-[9px] py-0">
                            En attente
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-3">
                        <div className="flex items-start gap-2">
                          <div className="w-9 h-9 rounded-lg bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden -mt-5 relative z-10 shadow-sm">
                            {store.logo_url ? (
                              <img src={store.logo_url} alt="" className="w-full h-full object-contain p-0.5" />
                            ) : (
                              <img src={gustoAvatarWhite} alt="" className="w-5 h-5 opacity-30" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-semibold text-sm text-charcoal truncate">{store.name || store.business_name}</h3>
                            <p className="text-[11px] text-muted-foreground truncate">{store.category_label}</p>
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1.5 truncate">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          {store.postal_code} {store.city}
                        </p>
                        {store.hours_today && (
                          <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Clock className="w-3 h-3 flex-shrink-0" />
                            {store.hours_today}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Boutique Gusto Section */}
      {shopProducts.length > 0 && (
        <section className="py-10 px-4 bg-gradient-to-b from-white to-lavender/5" data-testid="shop-section">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <Badge className="bg-primary/10 text-primary border-0 px-3 py-1.5 mb-2">
                  <ShoppingBag className="w-3 h-3 mr-1.5" />
                  Boutique
                </Badge>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-charcoal">
                  La Boutique du Chef
                </h2>
                <p className="text-sm text-muted-foreground mt-1">Payez en euros ou avec vos points (10 pts = 1€)</p>
              </div>
              <Link to="/shop">
                <Button variant="outline" className="rounded-full gap-2 hidden sm:flex">
                  Voir tout <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {shopProducts.slice(0, 6).map((product, i) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/shop/${product.id}`} data-testid={`home-product-${product.id}`}>
                    <Card className="overflow-hidden hover:shadow-md transition-all group cursor-pointer h-full">
                      <div className="aspect-square bg-muted relative overflow-hidden">
                        <img
                          src={product.featured_image}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <Badge className="absolute top-1.5 left-1.5 bg-primary text-white text-[10px] px-1.5">
                          {product.points_price} pts
                        </Badge>
                      </div>
                      <CardContent className="p-2">
                        <p className="text-xs font-medium text-charcoal truncate">{product.name}</p>
                        <p className="text-sm font-bold text-primary">{product.price.toFixed(2)}€</p>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-4 sm:hidden">
              <Link to="/shop">
                <Button className="rounded-full gap-2">
                  <ShoppingBag className="w-4 h-4" /> Voir toute la boutique
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Pros / Businesses Section */}
      <section className="py-10 px-4 bg-gradient-to-b from-lavender/10 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="bg-primary/10 text-primary border-0 px-4 py-2 mb-3">
              <Store className="w-3 h-3 mr-1.5" />
              Espace Professionnels
            </Badge>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-charcoal">
              Vous êtes commerçant ou restaurateur ?
            </h2>
            <p className="text-muted-foreground text-sm mt-2 max-w-lg mx-auto">
              Rejoignez Gusto pour toucher de nouveaux clients, gérer vos commandes et booster votre activité.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="border-lavender/30 hover:shadow-md transition-all">
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-lavender/30 flex items-center justify-center mb-3">
                  <Store className="w-6 h-6 text-charcoal" />
                </div>
                <h3 className="font-heading font-bold text-charcoal text-sm">Vitrine en ligne</h3>
                <p className="text-xs text-muted-foreground mt-1">Présentez votre boutique et vos produits à la communauté Gusto</p>
              </CardContent>
            </Card>
            <Card className="border-lavender/30 hover:shadow-md transition-all">
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-bold text-charcoal text-sm">Click & Collect</h3>
                <p className="text-xs text-muted-foreground mt-1">Recevez des commandes et organisez vos créneaux de retrait</p>
              </CardContent>
            </Card>
            <Card className="border-lavender/30 hover:shadow-md transition-all">
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-3">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-heading font-bold text-charcoal text-sm">Communauté locale</h3>
                <p className="text-xs text-muted-foreground mt-1">Fidélisez vos clients avec des offres exclusives et personnalisées</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Link to={user ? "/business/register" : "/register"}>
              <Button className="rounded-full px-6 bg-charcoal hover:bg-charcoal/90 text-sm gap-2">
                <Store className="w-4 h-4" />
                Inscrire mon commerce
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 px-4 bg-white">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-4 text-center">
          <div className="p-4 rounded-2xl bg-lavender/15">
            <p className="text-2xl md:text-3xl font-heading font-bold text-charcoal">{recipes.length}+</p>
            <p className="text-xs text-muted-foreground mt-1">Recettes créées</p>
          </div>
          <div className="p-4 rounded-2xl bg-primary/5">
            <p className="text-2xl md:text-3xl font-heading font-bold text-charcoal">
              <Star className="w-5 h-5 inline-block text-amber-500 -mt-1 mr-1" />IA
            </p>
            <p className="text-xs text-muted-foreground mt-1">Chef Gusto, votre allié</p>
          </div>
          <div className="p-4 rounded-2xl bg-green-50">
            <p className="text-2xl md:text-3xl font-heading font-bold text-charcoal">100%</p>
            <p className="text-xs text-muted-foreground mt-1">Gratuit</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 px-4 bg-gradient-to-br from-charcoal via-charcoal to-charcoal/95">
        <div className="max-w-4xl mx-auto text-center text-white">
          <GustoLogo size="lg" variant="dark" className="mx-auto mb-4" />
          <h2 className="font-heading text-xl md:text-2xl font-bold mb-2">
            Prêt(e) à cuisiner ?
          </h2>
          <p className="text-white/70 text-sm mb-6 max-w-md mx-auto">
            Rejoignez la communauté Gusto et transformez votre façon de cuisiner.
          </p>
          <Link to={user ? "/discover" : "/register"}>
            <Button className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 h-11 shadow-lg text-sm">
              {user ? "Explorer" : "Créer mon compte"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <GustoFooter />
    </div>
  );
};

export default HomePage;
